import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  useMemo,
  memo,
} from "react";
import { useNavigate } from "react-router-dom";
import { PropTypes } from "prop-types";

import { bindActionCreators } from "@reduxjs/toolkit";
import { connect } from "react-redux";
import * as actions from "../actions";
import reactWrapper from "@harbor/elements/utils/react/wrapper";
import TableStore from "@harbor/elements/utils/table/store";
import useColumns from "@harbor/elements/utils/table/hooks/columns";
import AppHOC from "../../../generics/AppHOC";
import Spinner from "../../../common/Spinner";
import NoData from "../../../common/table/NoData";
import SmartAccountTab from "../SmartAccountTab";
import AccountsFilter from "../AccountsFilter";
import AccountsModal from "../AccountsModal";
import { getAccountTableColumns } from "./tableConfig";
import apiService from "../../../config/api-config";
import { getTimePeriodELT } from "../../../utils/displayTime";
import {
  setLocalStorageFlag,
  getPostLoginNotifications,
  createMarkup,
  scrollToTop,
} from "../../../utils/common";
import { toast as showToast } from "../../../utils/genericCommon";
import { useI18n } from "../../../i18n/useI18n";
import css from "../accountsMagneticStyle.less";
import { getCookie } from "../../../serviceWorker";
import { IDP_ACTIONS, routeURLs } from "../../../utils/enums";

const [
  HbrCard,
  HbrModal,
  HbrButton,
  HbrIcon,
  HbrSpinner,
  HbrBanner,
  HbrInput,
  HbrTable,
  HbrTag,
  HbrPagination,
] = reactWrapper([
  "hbr-card",
  "hbr-modal",
  "hbr-button",
  "hbr-icon",
  "hbr-spinner",
  "hbr-banner",
  "hbr-input",
  "hbr-table",
  "hbr-tag",
  "hbr-pagination",
]);

const setSmartAccountReference = (accounts) => {
  accounts.forEach(({ overlays, ...obj }) => {
    overlays.forEach((item) => (item.smartAccountReference = obj));
  });
  return accounts.slice(0);
};

const filterAccounts = (data, searchTxt = "", statusFilter = "ALL") => {
  let filteredData = JSON.parse(JSON.stringify(data));
  const searchText = searchTxt.toLowerCase();
  if (searchText !== "")
    filteredData = filteredData.filter((item) => {
      item.overlays = Array.isArray(item.overlays)
        ? item.overlays.filter((val) => val.org_name?.includes(searchText))
        : [];
      return item.overlays.length > 0;
    });
  if (statusFilter !== "ALL")
    filteredData = filteredData.filter((item) => {
      item.overlays = Array.isArray(item.overlays)
        ? item.overlays.filter((item) => item.status === statusFilter)
        : [];
      return item.overlays.length > 0;
    });
  if (filteredData.length === 0) filteredData = [];
  return filteredData;
};

const AccountView = ({
  globalFilter,
  actions,
  accounts,
  setAccounts,
  setOverlays,
  setPostLoginNotifications,
  setSelectedSite,
  setCtrlTimeFilter,
  setAltoProps,
}) => {
  const { tViews } = useI18n("views");
  const {
    setShowModal,
    getIDP,
    displaySpinner,
    deleteIdp,
    isIdpDeleted,
    updateAccount,
    isAccountUpdated,
  } = actions;

  const refModal = useRef(null);
  const refPromptCancelButton = useRef(null);

  const setIntervalRefs = useRef({
    setOverlaysRef: null,
  });
  const navigate = useNavigate();
  const [smartAccounts, setSmartAccounts] = useState([]);
  const [showSpinner, setSpinner] = useState(false);
  const [prompt, setPrompt] = useState({
    showPrompt: false,
    message: "",
    data: {},
    buttons: [],
    title: "",
  });
  const { isSALoadedandNoData, isSupportRole } = globalFilter;
  const [supportAccountState, setSupportAccountState] = useState({
    filteredSaList: [],
    originalSaList: [],
    isLoaded: false,
  });
  const [selectedTab, setSelectedTab] = useState("smartAccountTab");
  const [smartAccountName, setSmartAccountName] = useState("");
  const [domainAccount, setDomainAccount] = useState();
  const [isSupportRoleSpinner, setIsSupportRoleSpinner] = useState(true);
  const isDomainAccountTab = selectedTab === "domainAccountTab";
  const [errorState, setErrorState] = useState({ isError: false });
  const accountFilterRef = useRef("ALL");
  const searchFilterValueRef = useRef("");
  const saRef = useRef(
    JSON.parse(JSON.stringify(setSmartAccountReference(globalFilter.accounts))),
  );
  const daRef = useRef([]);

  useEffect(() => {
    if (globalFilter.features.error !== null) scrollToTop();
  }, []);

  useEffect(() => {
    document.body.classList.add("accounts-toast-bottom-right");

    return () => {
      document.body.classList.remove("accounts-toast-bottom-right");
    };
  }, []);

  useEffect(() => {
    setSpinner(false);
  }, [accounts.showModal]);

  useEffect(() => {
    const runEffect = async () => {
      if (accounts.idpDeleted || accounts.accountUpdated) {
        let accountData = [];
        let vaAccountsData = [];
        if (selectedTab === "domainAccountTab") {
          accountData = await apiService.getDomainAccountInfo(smartAccountName);
          vaAccountsData = accountData?.data?.data || [];
          daRef.current = setSmartAccountReference(vaAccountsData);
          setDomainAccount(
            filterAccounts(
              daRef.current,
              searchFilterValueRef.current,
              accountFilterRef.current,
              tViews,
            ),
          );
        } else {
          accountData = await apiService.getSmartAccountData();
          vaAccountsData = accountData?.data?.data || [];
          setSmartAccounts(
            filterAccounts(
              setSmartAccountReference(vaAccountsData),
              searchFilterValueRef.current,
              accountFilterRef.current,
              tViews,
            ),
          );
          setAccounts(vaAccountsData);
        }

        setSpinner(false);
        setPrompt((values) => ({
          ...values,
          showPrompt: false,
          message: "",
          data: {},
          buttons: [],
          title: "",
        }));
        if (accounts.accountUpdated) {
          showToast(tViews("idpConfigUpdateSuccessMessage"), "success");
        }
        if (accounts.idpDeleted) {
          showToast(tViews("idpConfigDeleteSuccessMessage"), "success");
        }
      }
    };

    runEffect();
  }, [accounts.idpDeleted, accounts.accountUpdated]);

  useEffect(() => {
    if (!isDomainAccountTab) {
      const saveAccounts = setSmartAccountReference(globalFilter.accounts);
      setSmartAccounts(saveAccounts);
    }
  }, [selectedTab]);

  const onOverlaySelect = async (row) => {
    const isUIconvergence = getCookie("cl-analytics");

    let defaultTimePeriodSelected = isUIconvergence ? "1" : "12";

    localStorage.removeItem("bandForecastingTab");
    localStorage.removeItem("onboardingStatus");
    localStorage.removeItem("selectedCircuit");
    localStorage.removeItem("circuit");

    setLocalStorageFlag("currentOverlay", row.overlay_id);
    setLocalStorageFlag("currentOverlayName", row.org_name);
    setLocalStorageFlag("roles", JSON.stringify(row.roles));
    setLocalStorageFlag("timePeriodSelected", defaultTimePeriodSelected);

    if (isSupportRole) {
      let overlays = [];

      if (selectedTab !== "domainAccountTab") {
        overlays = smartAccounts
          .flatMap((smartAccount) => smartAccount.overlays)
          .filter((data) => data.overlay_id)
          .map((data) => ({
            key: data.overlay_id,
            value: data.org_name,
            status: data.status,
            roles: data.roles,
          }));
      } else {
        overlays =
          domainAccount && domainAccount[0]
            ? domainAccount[0].overlays
                .filter((data) => data.overlay_id)
                .map((data) => ({
                  key: data.overlay_id,
                  value: data.org_name,
                  status: data.status,
                  roles: data.roles,
                }))
            : [];
      }
      overlays?.sort((a, b) =>
        a.value.toLowerCase() > b.value.toLowerCase() ? 1 : -1,
      );
      clearTimeout(setIntervalRefs.current.setOverlaysRef);
      setIntervalRefs.current.setOverlaysRef = setTimeout(
        () => setOverlays(overlays),
        0,
      );
    }
    getPostLoginNotifications(setPostLoginNotifications);
    navigate(routeURLs.overview);
  };

  // Clear the timeout when the component unmounts
  useEffect(() => {
    const isUIconvergence = getCookie("cl-analytics");

    let defaultTimePeriodSelected = isUIconvergence ? "1" : "12";

    setSelectedSite(-1);

    const timePeriod = getTimePeriodELT(defaultTimePeriodSelected);

    setAltoProps({ predictiveTabSelected: "pprTab", status: "" });
    setCtrlTimeFilter(timePeriod, defaultTimePeriodSelected);

    return () => {
      clearTimeout(setIntervalRefs.current.setOverlaysRef);
    };
  }, []);

  const searchOverlays = (event) => {
    const { value } = event.currentTarget || "";
    const searchSmartAccounts = JSON.parse(JSON.stringify(saRef.current));
    searchFilterValueRef.current = value;
    const smartAcc = searchSmartAccounts.filter((sa) => {
      // checks if sa has va's matching the searched string
      const hasValidVa =
        sa.overlays &&
        sa.overlays.some(
          (va) =>
            va.org_name.toLowerCase().includes(value.toLowerCase()) &&
            (accountFilterRef.current === "ALL" ||
              va.status === accountFilterRef.current),
        );
      if (hasValidVa) {
        sa.overlays = sa.overlays.filter(
          (va) =>
            va.org_name.toLowerCase().includes(value.toLowerCase()) &&
            (accountFilterRef.current === "ALL" ||
              va.status === accountFilterRef.current),
        );
        return true;
      }
      return false;
    });
    setSmartAccounts(smartAcc);
  };

  const filterOverlays = (value) => {
    let accountArray = [];
    accountFilterRef.current = value;
    if (isDomainAccountTab) {
      accountArray = JSON.parse(JSON.stringify(daRef.current));
    } else {
      accountArray = JSON.parse(JSON.stringify(saRef.current));
    }

    if (searchFilterValueRef.current === "" && value === "ALL") {
      isDomainAccountTab
        ? setDomainAccount(
            filterAccounts(
              daRef.current,
              searchFilterValueRef.current,
              accountFilterRef.current,
              tViews,
            ),
          )
        : setSmartAccounts(
            filterAccounts(
              saRef.current,
              searchFilterValueRef.current,
              accountFilterRef.current,
              tViews,
            ),
          );
      return;
    }

    const filteredAccounts = accountArray.filter((account) => {
      // checks if account has va's matching the searched string
      const hasValidVa =
        account.overlays &&
        account.overlays.some(
          (va) =>
            va.org_name
              .toLowerCase()
              .includes(searchFilterValueRef.current.toLowerCase()) &&
            (value === "ALL" || va.status === value),
        );
      if (hasValidVa) {
        account.overlays = account.overlays.filter(
          (va) =>
            va.org_name
              .toLowerCase()
              .includes(searchFilterValueRef.current.toLowerCase()) &&
            (accountFilterRef.current === "ALL" ||
              va.status === accountFilterRef.current),
        );
        return true;
      }
      return false;
    });
    isDomainAccountTab
      ? setDomainAccount(filteredAccounts)
      : setSmartAccounts(filteredAccounts);
  };

  const handleActionClick = (e, row) => {
    // If overlay actions
    const status = typeof e === "string" ? e : e?.target?.innerText;
    if (!status) return;
    const overlay = { ...row };
    overlay["idpAction"] = status;
    if (status === IDP_ACTIONS.DEFINE) {
      setShowModal(overlay);
    } else if (status === IDP_ACTIONS.VIEW || status === IDP_ACTIONS.EDIT) {
      const {
        idp_type,
        idp_id,
        va_id,
        overlay_id,
        smartAccountReference,
        domain_id,
      } = overlay;
      const query = {};
      query.idp_type = idp_type;
      query.idp_id = idp_id;
      query.va_id = va_id;
      query.overlay_id = overlay_id;
      if (smartAccountReference) {
        query.sa_domain = smartAccountReference.domain_id;
      } else {
        if (!domain_id && domainAccount.length) {
          query.sa_domain = domainAccount[0].domain_id;
          query.domain_id = domainAccount[0].domain_id;
        } else {
          query.sa_domain = domain_id;
          query.domain_id = domain_id;
        }
      }
      setShowModal(overlay);
      displaySpinner({
        showSpinner: true,
      });
      getIDP(query, overlay);
    } else if (status === IDP_ACTIONS.DELETE) {
      isIdpDeleted({
        idpDeleted: false,
      });
      const {
        org_name,
        smartAccountReference,
        name,
        idp_id,
        domain_id,
        va_id,
      } = overlay;
      const alertMessage =
        org_name && org_name !== ""
          ? smartAccountReference.name + " > " + org_name
          : name;
      let payload = {
        idp_id: idp_id,
        sa_domain: domain_id ? domain_id : smartAccountReference.domain_id,
      };
      if (!domain_id) {
        payload["va_id"] = va_id;
      }
      setPrompt((values) => ({
        ...values,
        showPrompt: true,
        message:
          tViews("idpConfigDeleteIdpPromptMessage") +
          " " +
          `<b class='alert-message'>${alertMessage}</b>`,
        data: payload,
        buttons: [tViews("reporTableCancel"), tViews("reporTableDelete")],
        title: tViews("reporTableDelete"),
      }));
    } else if (status === IDP_ACTIONS.ACTIVATE || status === "Activated") {
      isAccountUpdated({
        accountUpdated: false,
      });
      const {
        org_name,
        smartAccountReference,
        name,
        domain_id,
        va_id,
        overlay_id,
      } = overlay;
      const alertMessage =
        org_name && org_name !== ""
          ? smartAccountReference.name + " > " + org_name
          : name;

      let payload = {
        query: {
          rules: {
            o_id: overlay_id,
          },
        },
        update_field: {
          status: "Active",
        },
        sa_domain: domain_id ? domain_id : smartAccountReference?.domain_id,
        va_id: va_id,
      };

      setPrompt((values) => ({
        ...values,
        showPrompt: true,
        message:
          tViews("idpConfigActivateOverlayPromptMessage") +
          " " +
          `<b class='alert-message'>${alertMessage}</b>`,
        data: payload,
        buttons: [tViews("reporTableCancel"), tViews("activate")],
        title: tViews("activate"),
      }));
    } else if (status === IDP_ACTIONS.DEACTIVATE || status === "Inactive") {
      isAccountUpdated({
        accountUpdated: false,
      });
      const {
        org_name,
        smartAccountReference,
        name,
        domain_id,
        va_id,
        overlay_id,
      } = overlay;
      const alertMessage =
        org_name && org_name !== ""
          ? smartAccountReference.name + " > " + org_name
          : name;

      let payload = {
        query: {
          rules: {
            o_id: overlay_id,
          },
        },
        update_field: {
          status: "Inactive",
        },
        sa_domain: domain_id ? domain_id : smartAccountReference?.domain_id,
        va_id: va_id,
      };

      setPrompt((values) => ({
        ...values,
        showPrompt: true,
        message:
          tViews("idpConfigDeactivateOverlayPromptMessage") +
          " " +
          `<b class='alert-message'>${alertMessage}</b>`,
        data: payload,
        buttons: [tViews("reporTableCancel"), tViews("deActivate")],
        title: tViews("deActivate"),
      }));
    }
  };

  // Hide Model
  const hideAlert = useCallback(
    () =>
      setPrompt((values) => ({
        ...values,
        showPrompt: false,
        message: "",
        buttons: [],
        title: "",
      })),
    [],
  );

  const onClickCancel = () => {
    refModal.current.element.hide();
    hideAlert();
  };

  const overlayDismiss = (event) => {
    event.preventDefault();
  };

  const initialPromptFocus = (event) => {
    event.preventDefault();
    refPromptCancelButton.current?.element?.setFocus?.({ preventScroll: true });
  };

  const focusPromptCancelButton = () => {
    setTimeout(() => {
      refPromptCancelButton.current?.element?.setFocus?.({
        preventScroll: true,
      });
    }, 0);
  };

  useEffect(() => {
    if (!prompt.showPrompt) return undefined;

    const handlePromptEscape = (event) => {
      if (event.key === "Escape") {
        refModal.current?.element?.hide?.();
        hideAlert();
      }
    };

    window.addEventListener("keydown", handlePromptEscape);

    return () => {
      window.removeEventListener("keydown", handlePromptEscape);
    };
  }, [prompt.showPrompt, hideAlert]);

  const onSubmit = (event, title) => {
    setSpinner(true);
    if (title === "Delete") {
      deleteIdp(prompt.data, hideAlert);
    }
    if (title === IDP_ACTIONS.ACTIVATE || title === IDP_ACTIONS.DEACTIVATE) {
      updateAccount(prompt.data, hideAlert);
    }
  };

  const getOverlayActions = (row) => {
    const hasOverlayId = row.overlay_id && row.overlay_id.length;
    const actions = [];
    const idpValid = row.roles?.includes("admin") && hasOverlayId;

    if (idpValid && row.idp_defined === "False") {
      actions.push({
        label: IDP_ACTIONS.DEFINE,
        name: "define-idp-menu-button",
      });
    }
    if (idpValid && row.status === "ACTIVE") {
      actions.push({
        label: IDP_ACTIONS.DEACTIVATE,
        name: "deactivate-idp-menu-button",
      });
    }
    if (row.status === "DEACTIVATED" || row.status === "INACTIVE") {
      actions.push({
        label: IDP_ACTIONS.ACTIVATE,
        name: "activate-idp-menu-button",
      });
    }
    if (row.idp_defined === "True") {
      actions.push({
        label: IDP_ACTIONS.VIEW,
        name: "view-idp-menu-button",
      });
      actions.push({
        label: IDP_ACTIONS.EDIT,
        name: "edit-idp-menu-button",
      });
      actions.push({
        label: IDP_ACTIONS.DELETE,
        name: "delete-idp-menu-button",
      });
    }
    return row.status === "NEW" ? [] : actions;
  };

  const getSmartAccountActionButtons = (account) => {
    if (!(account?.roles && account.roles.includes("admin"))) return [];
    if (account.idp_defined === "True") {
      return [IDP_ACTIONS.EDIT, IDP_ACTIONS.DELETE];
    }
    return [IDP_ACTIONS.DEFINE];
  };

  const AccountOverlaysTable = ({ account, idPrefix, tableIndex }) => {
    const [, forceUpdate] = useState(null);
    const defaultSort = [["org_name", "asc"]];
    const columnsConfig = useMemo(
      () =>
        getAccountTableColumns({
          tViews,
          onOverlaySelect,
          getOverlayActions,
          handleActionClick,
        }),
      [tViews, onOverlaySelect, getOverlayActions, handleActionClick],
    );

    const columnsHook = useColumns({
      config: () => columnsConfig,
      order: defaultSort,
    });

    const tableStore = useMemo(() => {
      const overlays = Array.isArray(account?.overlays) ? account.overlays : [];

      const store = new TableStore({
        data: overlays,
        query: { page: 0, pageLength: 30 },
        hooks: [columnsHook],
        size: "spacious",
      });
      return store;
    }, [account]);

    useEffect(() => {
      const unsubscribe = tableStore.subscribe(forceUpdate);
      return () => unsubscribe();
    }, [tableStore]);

    const { source, columns, query, count } = tableStore.getState();
    const { pageLength, page } = query;

    return (
      <>
        <HbrTable
          id={`${idPrefix}-table-${tableIndex}`}
          className="accounts-overlay-table"
          data-testid={`${idPrefix}-table-${tableIndex}`}
          columns={columns}
          source={source}
          readonly
          resize
          canFocus={false}
          useClipboard={false}
          headerSlot
          serverSide
          externalSorting
          frameSize={source.length}
          onHbr-before-sort={(event) => {
            const { column, order } = event.detail;
            const columnsHook = tableStore.getHook("columns");
            if (order) columnsHook.sort({ order: [[column.id, order]] });
            else if (column.id === defaultSort[0][0]) {
              columnsHook.sort({ order: [[column.id, "asc"]] });
            } else {
              columnsHook.sort({ order: defaultSort });
            }
          }}
        >
          <NoData
            count={source.length}
            loader={false}
            error={null}
            text={tViews("idpConfigNoDataAvailable")}
          />
        </HbrTable>
        <HbrPagination
          aria-label={tViews("a11yLabels.paginationAriaLabel")}
          pageSize={pageLength}
          itemCount={count}
          page={page}
          tableStore={tableStore}
          showAll
          orderArray={[1, 2, 0]}
          size="small"
          sizeChanger={false}
          onHbr-page-changed={(event) => {
            const nextPage = Number.isInteger(event.detail) ? event.detail : 0;
            tableStore.setQuery({ page: nextPage });
          }}
          onHbr-size-changed={(event) => {
            tableStore.setQuery({ pageLength: event.detail.pageSize, page: 0 });
          }}
        />
      </>
    );
  };

  AccountOverlaysTable.propTypes = {
    account: accountItemPropType,
    idPrefix: PropTypes.string.isRequired,
    tableIndex: PropTypes.number.isRequired,
  };

  const AccountSectionCard = ({ account, index, idPrefix }) => {
    const smartAccountActions = getSmartAccountActionButtons(account);
    const accountName =
      account?.name && String(account.name).trim() ? account.name : "undefined";
    const overlayCount = Array.isArray(account?.overlays)
      ? account.overlays.length
      : 0;
    return (
      <div key={index} className="accounts-table" data-cy="accountsTableView">
        <HbrCard className="hbr-card-accounts">
          <div className="hbr-css__layout-col-sm">
            <div className={css["smart-account-header"]}>
              <div
                className="hbr-type-h2 accountNameHeading"
                role="heading"
                aria-level={2}
              >
                {accountName} <HbrTag>{overlayCount}</HbrTag>
              </div>
              {!!smartAccountActions.length && (
                <div className={css["smart-account-idp"]}>
                  <HbrTag accent="var(--hbr-color-accent-c-weak)">
                    {account.idp_defined === "True"
                      ? tViews("idpConfigIdpDefined")
                      : tViews("idpConfigIdpNotDefined")}
                  </HbrTag>
                  <div className={css["smart-account-actions"]}>
                    {smartAccountActions.map((actionLabel) => (
                      <HbrButton
                        key={`${account.domain_id}-${actionLabel}`}
                        name={account.domain_id}
                        id={account.domain_id}
                        onClick={() => handleActionClick(actionLabel, account)}
                      >
                        {actionLabel}
                      </HbrButton>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
          <AccountOverlaysTable
            account={account}
            idPrefix={idPrefix}
            tableIndex={index}
          />
        </HbrCard>
      </div>
    );
  };

  AccountSectionCard.propTypes = {
    account: accountItemPropType,
    index: PropTypes.number.isRequired,
    idPrefix: PropTypes.string.isRequired,
  };

  /*  when data is empty, to show atleast one card with table headers and no data text */

  const EmptyAccountsTable = ({ idPrefix }) => {
    const emptyAccount = { overlays: [] };
    return (
      <div className="accounts-table" data-cy="accountsTableView">
        <HbrCard className="hbr-card-accounts">
          <div className="hbr-css__layout-col-sm">
            <div className={css["smart-account-header"]}>
              <div
                className="hbr-type-h2 accountNameHeading"
                role="heading"
                aria-level={2}
              >
                {"undefined"} <HbrTag>0</HbrTag>
              </div>
            </div>
          </div>
          <AccountOverlaysTable
            account={emptyAccount}
            idPrefix={idPrefix}
            tableIndex={0}
          />
        </HbrCard>
      </div>
    );
  };

  EmptyAccountsTable.propTypes = {
    idPrefix: PropTypes.string.isRequired,
  };

  const onMenuOpen = (state) => {
    if (state.errorObject) {
      setErrorState({
        isError: true,
        message: `${state.errorObject.message}.
        Failed with error code ${state.errorObject.errorCode}.
        Try again. Reload the page.`,
      });

      setSupportAccountState({
        ...supportAccountState,
        ...state,
      });
    } else {
      setSupportAccountState({
        ...supportAccountState,
        ...state,
      });
    }
  };

  const onTabsChange = async (tab) => {
    searchFilterValueRef.current = "";
    accountFilterRef.current = "ALL";
    if (tab.name === "domainAccountTab") {
      setSelectedTab(tab.name);
      setIsSupportRoleSpinner(true);
      setSmartAccountName(tab.smartAccountName);

      const accountDetails = await apiService.getDomainAccountInfo(
        tab.smartAccountName,
      );

      if (!accountDetails.errorObject) {
        const saVaAccounts = setSmartAccountReference(accountDetails.data.data);
        daRef.current = setSmartAccountReference(accountDetails.data.data);
        setDomainAccount(saVaAccounts);
      } else {
        const emptyAccount = [
          {
            domain_id: "",
            idp_defined: "True",
            idp_id: "",
            idp_level: "",
            idp_status: "",
            idp_type: "",
            name: "",
            overlays: [],
          },
        ];
        daRef.current = emptyAccount;
        setDomainAccount(emptyAccount);
        setErrorState({
          isError: true,
          message: `${accountDetails.errorObject.message}.
          Failed with error code ${accountDetails.errorObject.errorCode}.
          Select a new domain account from the Support User Acccount`,
        });
      }

      setIsSupportRoleSpinner(false);
    } else {
      setSelectedTab(tab.name);
    }
  };

  useEffect(() => {
    isIdpDeleted({
      idpDeleted: false,
    });
    const saVaAccounts = setSmartAccountReference(globalFilter.accounts);

    if (isSALoadedandNoData) {
      saRef.current = structuredClone(saVaAccounts);
      const filteredAccounts = filterAccounts(
        saRef.current,
        searchFilterValueRef.current,
        accountFilterRef.current,
        tViews,
      );
      setSmartAccounts((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(filteredAccounts)) {
          return filteredAccounts;
        }
        return prev;
      });
    } else {
      setSmartAccounts((prev) => {
        if (JSON.stringify(prev) !== JSON.stringify(saVaAccounts)) {
          return saVaAccounts;
        }
        return prev;
      });
    }
  }, [isSALoadedandNoData, globalFilter.accounts]);

  if (isSALoadedandNoData) {
    return (
      <>
        {globalFilter.features.error !== null && (
          <div className="domain-account-banner">
            <HbrBanner sentiment="danger" variant="fill" open closable>
              <HbrIcon slot="icon" name="alerts-negative-large" />
              {tViews("licenseErrorMsg")}
            </HbrBanner>
          </div>
        )}
        <main id="main-content">
          <div className="smart-accounts-container">
            <div className={css["smart-accounts"]}>
              {errorState.isError && (
                <div className="domain-account-banner">
                  <HbrBanner sentiment="danger" variant="fill" open closable>
                    <HbrIcon slot="icon" name="alerts-negative-large" />
                    {errorState.message}
                  </HbrBanner>
                </div>
              )}
              {!prompt.showPrompt && showSpinner && (
                <div data-cy="spinner-accounts-view">
                  <Spinner />
                </div>
              )}
              {prompt.showPrompt && (
                <HbrModal
                  ref={refModal}
                  open={true}
                  onHbr-initial-focus={initialPromptFocus}
                  onHbr-after-show={focusPromptCancelButton}
                  onHbr-hide={hideAlert}
                  onHbr-overlay-dismiss={overlayDismiss}
                  id="accounts-actions-modal"
                >
                  <div slot="label">
                    <HbrIcon
                      name="alerts-warning-large"
                      sentiment="warning"
                    ></HbrIcon>{" "}
                    {prompt.title}
                  </div>
                  <div dangerouslySetInnerHTML={createMarkup(prompt.message)} />
                  <div slot="footer">
                    <HbrButton
                      ref={refPromptCancelButton}
                      variant="text"
                      style={{ marginRight: "16px" }}
                      onClick={onClickCancel}
                      disabled={showSpinner}
                    >
                      {prompt.buttons[0]}
                    </HbrButton>
                    <HbrButton
                      variant="fill"
                      onClick={(e) => onSubmit(e, prompt.title)}
                      sentiment={
                        prompt.buttons[1] === IDP_ACTIONS.ACTIVATE
                          ? ""
                          : "danger"
                      }
                      disabled={showSpinner}
                    >
                      {showSpinner && <HbrSpinner slot="prefix"></HbrSpinner>}
                      {prompt.buttons[1]}
                    </HbrButton>
                  </div>
                </HbrModal>
              )}
              {accounts.showModal && <AccountsModal />}
              {isSupportRole && supportAccountState.originalSaList ? (
                <div className="smart-account-tabs">
                  <SmartAccountTab
                    onMenuOpen={onMenuOpen}
                    onTabsChange={onTabsChange}
                    supportAccountState={supportAccountState}
                    selectedTab={selectedTab}
                  />
                </div>
              ) : (
                <div className="hbr-type-h2" role="heading" aria-level={"1"}>
                  {tViews("smartAccount")}
                </div>
              )}
              {!isDomainAccountTab && (
                <>
                  <div className="table-search">
                    <HbrInput
                      size="medium"
                      clearable={true}
                      className="hbr-css__form-section-md"
                      data-testid="activated"
                      placeholder={tViews("smartAccountSearch")}
                      onHbr-input={
                        searchOverlays
                      } /* fires on every input change */
                    >
                      <HbrIcon
                        slot="prefix"
                        name="magnifying-glass"
                        size="16px"
                        staticColors={true}
                      />
                      <HbrIcon
                        slot="clear-icon"
                        name="hbr-delete-content"
                        size="16px"
                        staticColors={true}
                      />
                    </HbrInput>
                  </div>
                  <div>
                    <AccountsFilter filterOverlays={filterOverlays} />
                  </div>
                </>
              )}
              {isDomainAccountTab && domainAccount && (
                <AccountsFilter filterOverlays={filterOverlays} />
              )}

              {!isDomainAccountTab ? (
                smartAccounts.length ? (
                  smartAccounts.map((account, index) => (
                    <AccountSectionCard
                      key={`account-${index}`}
                      account={account}
                      index={index}
                      idPrefix="account"
                    />
                  ))
                ) : (
                  <EmptyAccountsTable idPrefix="account-empty" />
                )
              ) : isSupportRoleSpinner ? (
                <div className="spinner-container">
                  <Spinner />
                </div>
              ) : domainAccount && domainAccount.length ? (
                domainAccount.map((account, index) => (
                  <AccountSectionCard
                    key={`domain-account-${index}`}
                    account={account}
                    index={index}
                    idPrefix="domain-account"
                  />
                ))
              ) : (
                <EmptyAccountsTable idPrefix="domain-account-empty" />
              )}
            </div>
          </div>
        </main>
      </>
    );
  } else {
    return (
      <div className="spinner-container">
        <Spinner />
      </div>
    );
  }
};

const arePropsEqual = (prevProps, nextProps) => {
  // Destructure the relevant properties from prevProps and nextProps
  const {
    globalFilter: {
      accounts: prevAccounts,
      isSALoadedandNoData: prevIsSALoadedandNoData,
    },
    accounts: {
      showModal: prevShowModal,
      idpDeleted: prevIdpDeleted,
      accountUpdated: prevAccountUpdated,
    },
  } = prevProps;

  const {
    globalFilter: {
      accounts: nextAccounts,
      isSALoadedandNoData: nextIsSALoadedandNoData,
    },
    accounts: {
      showModal: nextShowModal,
      idpDeleted: nextIdpDeleted,
      accountUpdated: nextAccountUpdated,
    },
  } = nextProps;
  // Compare the relevant properties for memoization
  const arrayAccountsEqual =
    JSON.stringify(prevAccounts) === JSON.stringify(nextAccounts);
  const accountsLengthEqual = prevAccounts.length === nextAccounts.length;
  const showModalEqual = prevShowModal === nextShowModal;
  const idpDeletedEqual = prevIdpDeleted === nextIdpDeleted;
  const accountUpdatedEqual = prevAccountUpdated === nextAccountUpdated;
  const isSALoadedandNoDataEqual =
    prevIsSALoadedandNoData === nextIsSALoadedandNoData;
  // Return true only if all properties are equal, memoizing the component
  return (
    arrayAccountsEqual &&
    accountsLengthEqual &&
    showModalEqual &&
    idpDeletedEqual &&
    accountUpdatedEqual &&
    isSALoadedandNoDataEqual
  );
};

const getDisplayName = (WrappedComponent) =>
  WrappedComponent.displayName || WrappedComponent.name || "Component";

const withNoRerendering = (WrappedComponent) => {
  const MemoizedComponent = memo(
    (props) => <WrappedComponent {...props} />,
    arePropsEqual,
  );
  MemoizedComponent.displayName = `WithNoRerendering(${getDisplayName(
    WrappedComponent,
  )})`;
  return MemoizedComponent;
};

const MemoizedAccountView = withNoRerendering(AccountView);

const accountItemPropType = PropTypes.shape({
  name: PropTypes.string,
  domain_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  idp_defined: PropTypes.string,
  roles: PropTypes.array,
  overlays: PropTypes.array,
});

AccountView.propTypes = {
  globalFilter: PropTypes.object.isRequired,
  actions: PropTypes.object.isRequired,
  accounts: PropTypes.object,
  setAccounts: PropTypes.func,
  setCtrlTimeFilter: PropTypes.func,
  setOverlays: PropTypes.func,
  setPostLoginNotifications: PropTypes.func,
  setAggregateAppData: PropTypes.func.isRequired,
  setAppFamilySLA: PropTypes.func.isRequired,
  setSelectedSite: PropTypes.func,
  setAltoProps: PropTypes.func,
};

const mapDispatchToProps = (dispatch) => ({
  actions: bindActionCreators(actions, dispatch),
});

const mapStateToProps = (state) => ({ accounts: state.vanalytics.accounts });

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AppHOC(MemoizedAccountView));
